import React, { FC, useContext, useRef, useState } from 'react';

// Components
import Form from '../../components/form/form';
import StudentSelector from '../../components/student-selector/student-selector';

// Form Data
import { formDataEventCreate } from '../../form-data/form-data-event-create';

// Helpers
import {
  forceRemovePreviewImage,
  handleFormOnChange,
  handleStudentChange,
  isFormComplete,
  useRevokePreviewUrlOnUnmount,
} from '../../helpers/event-helper';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import FileStorageService from '../../services/file-storage-service';

export const useEventForm = (initialData: () => EventFormData) => {
  const [data, setData] = useState<EventFormData>(initialData);
  const [validations, setValidations] = useState({});
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useRevokePreviewUrlOnUnmount(data);

  // O modal de agendamento guarda a função de envio de quando foi aberto, então
  // tanto a trava quanto os dados enviados vêm de refs, não do estado capturado.
  const submitting = useRef(false);
  const latest = useRef(data);
  latest.current = data;

  // Um duplo clique criaria dois eventos, cada um com o seu push.
  const submitOnce = (send: (current: EventFormData) => Promise<void>): Promise<void> => {
    if (submitting.current) return Promise.resolve();
    submitting.current = true;
    setIsSubmitting(true);

    return send(latest.current).catch((error) => {
      submitting.current = false;
      setIsSubmitting(false);
      throw error;
    });
  };

  return {
    data,
    setData,
    validations,
    setValidations,
    isLoadingImage,
    setIsLoadingImage,
    allowSubmit: isFormComplete(data, validations) && !isSubmitting,
    submitOnce,
  };
};

type EventForm = ReturnType<typeof useEventForm>;

// BaseContentContainer distribui os filhos por posição, então o formulário e o
// seletor de alunos são dois componentes, um para cada coluna.
export const EventFormInputs: FC<{
  form: EventForm;
  event?: EventDto;
  fileStorageService: FileStorageService;
}> = ({ form, event, fileStorageService }) => {
  const { setToastContent } = useContext(GeneralContext);
  const { data, setData, validations, setValidations } = form;

  return (
    <Form
      formData={formDataEventCreate(event)}
      validations={validations}
      handleOnChange={(evt) =>
        handleFormOnChange(evt, {
          data,
          setData,
          setValidations,
          setIsLoadingImage: form.setIsLoadingImage,
          fileStorageService,
          setToastContent,
        })
      }
      previewImage={data.formData.image || null}
      forceRemovePreviewImage={() => forceRemovePreviewImage(setData)}
      isLoadingImage={form.isLoadingImage}
    />
  );
};

export const EventRecipients: FC<{
  form: EventForm;
  classroomList: ClassroomStudents[];
}> = ({ form, classroomList }) => {
  const { data, setData } = form;

  const selector = (
    <StudentSelector
      classrooms={classroomList}
      handleStudentChange={(selectedClassrooms: number[], selectedStudents: number[]) =>
        handleStudentChange(selectedClassrooms, selectedStudents, setData)
      }
      initialClassrooms={data.classData}
      initialStudents={data.studentData}
    />
  );

  if (data.unmatchedStudentUuids.length === 0) return selector;

  // O seletor ocupa 100% da altura e rola por dentro; numa coluna flex ele
  // encolhe para dar lugar ao aviso em vez de transbordar.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <p style={{ padding: '24px 40px 0', flexShrink: 0 }}>
        {data.unmatchedStudentUuids.length} aluno(s) que receberam este evento não estão nas
        turmas listadas e continuam como destinatários.
      </p>
      {selector}
    </div>
  );
};
